import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { api, API, detailFromError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel,
  AlertDialogContent, AlertDialogDescription, AlertDialogFooter,
  AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { CookieReimportDialog } from "@/components/CookieReimportDialog";

const VARIANT = {
  pending: "secondary",
  scraping: "default",
  downloading_pdfs: "default",
  packaging: "default",
  completed: "default",
  failed: "destructive",
  cookie_expired: "destructive",
  data_loss_detected: "destructive",
};

function formatBytes(n) {
  if (!n) return "0 B";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(2)} MB`;
}

// Phase 7 — extract the filename from a stored relative path like
// `pdfs/405-9060491-6251551/invoice-01.pdf`. Used for the per-file
// download link's ?filename= query.
function _fileTail(relPath) {
  if (!relPath) return "";
  const parts = String(relPath).split("/");
  return parts[parts.length - 1] || "";
}

// Phase 7 — multi-file PDF cell. Renders a per-file link for each
// invoice / credit-note PDF Amazon exposed. Legacy runs still fall
// back to the single-file "Open" link.
function PdfCell({ runId, order, runIsLegacy }) {
  const status = order.pdf_status;
  const filteredOut = !!order.filtered_out;
  const invoices = order.invoice_pdf_paths || [];
  const credits = order.credit_note_pdf_paths || [];
  const hasPhase7Pdfs = invoices.length > 0 || credits.length > 0;

  if (filteredOut) {
    return (
      <span
        className="text-muted-foreground text-xs italic"
        data-testid={`pdf-cell-${order.order_id}`}
      >
        Not processed
      </span>
    );
  }

  // Phase 7 downloaded case — one link per file, with counts.
  if (status === "downloaded" && hasPhase7Pdfs) {
    return (
      <div
        className="space-y-1"
        data-testid={`pdf-cell-${order.order_id}`}
      >
        <div className="text-[11px] text-emerald-700 dark:text-emerald-400 font-medium">
          {invoices.length} invoice{invoices.length === 1 ? "" : "s"}
          {credits.length > 0 && ` + ${credits.length} credit note${credits.length === 1 ? "" : "s"}`}
        </div>
        <div className="flex flex-wrap gap-2">
          {invoices.map((p, i) => {
            const fname = _fileTail(p);
            return (
              <a
                key={p}
                href={`${API}/runs/${runId}/pdfs/${order.order_id}?filename=${encodeURIComponent(fname)}`}
                target="_blank"
                rel="noreferrer"
                className="text-primary underline underline-offset-4 text-xs"
                data-testid={`pdf-invoice-link-${order.order_id}-${i + 1}`}
              >
                Invoice {i + 1}
              </a>
            );
          })}
          {credits.map((p, i) => {
            const fname = _fileTail(p);
            return (
              <a
                key={p}
                href={`${API}/runs/${runId}/pdfs/${order.order_id}?filename=${encodeURIComponent(fname)}`}
                target="_blank"
                rel="noreferrer"
                className="text-amber-700 dark:text-amber-400 underline underline-offset-4 text-xs"
                data-testid={`pdf-credit-note-link-${order.order_id}-${i + 1}`}
              >
                Credit note {i + 1}
              </a>
            );
          })}
        </div>
      </div>
    );
  }

  // Legacy downloaded case — single-file open link (pre-Phase-7 layout).
  if (status === "downloaded") {
    return (
      <a
        href={`${API}/runs/${runId}/pdfs/${order.order_id}`}
        target="_blank"
        rel="noreferrer"
        className="text-primary underline underline-offset-4 text-sm"
        data-testid={`pdf-cell-${order.order_id}`}
        title={`Open ${order.pdf_filename || `${order.order_id}.pdf`}`}
      >
        ✓ Open {runIsLegacy ? "(Order Summary)" : ""}
      </a>
    );
  }
  if (status === "skipped_no_invoice") {
    return (
      <span
        className="text-muted-foreground text-xs"
        data-testid={`pdf-cell-${order.order_id}`}
      >
        — No invoice
      </span>
    );
  }
  if (status === "skipped_not_yet") {
    return (
      <span
        className="text-muted-foreground text-xs"
        data-testid={`pdf-cell-${order.order_id}`}
      >
        — Not yet issued
      </span>
    );
  }
  if (status === "failed") {
    return (
      <span
        className="text-destructive text-xs font-medium"
        data-testid={`pdf-cell-${order.order_id}`}
      >
        ✗ Failed
      </span>
    );
  }
  return (
    <span className="text-muted-foreground text-xs" data-testid={`pdf-cell-${order.order_id}`}>
      Pending
    </span>
  );
}

// Phase 6/7 — per-order plain-English explanation. Reads pdf_skip_reason
// (populated by pdf_downloader). For "downloaded" status the cell is
// empty. For `filtered_out` orders (Phase 7 placed_by filter) we
// surface the filter reason.
function PdfReasonCell({ order }) {
  if (order.filtered_out) {
    return (
      <span
        className="text-xs leading-snug text-muted-foreground italic"
        data-testid={`pdf-reason-${order.order_id}`}
      >
        {order.filtered_out_reason ||
          "Placed by someone else on this account — the \"Only orders I placed myself\" filter excluded this order."}
      </span>
    );
  }
  const status = order.pdf_status;
  if (status === "downloaded") {
    return (
      <span className="text-muted-foreground text-xs" data-testid={`pdf-reason-${order.order_id}`}>
        —
      </span>
    );
  }
  const reason = order.pdf_skip_reason;
  if (!reason) {
    return (
      <span className="text-muted-foreground text-xs italic" data-testid={`pdf-reason-${order.order_id}`}>
        Reason not recorded for this run.
      </span>
    );
  }
  const isFailure = status === "failed";
  return (
    <span
      className={`text-xs leading-snug ${isFailure ? "text-amber-700 dark:text-amber-400" : "text-muted-foreground"}`}
      data-testid={`pdf-reason-${order.order_id}`}
    >
      {reason}
    </span>
  );
}

// Phase 6/7 — completion summary panel with per-bucket skip breakdown.
// Language is intentionally strict about ORDERS vs INVOICES because the
// user was confusing order-count (72) with invoice-count (~100).
function CompletionPanel({ run }) {
  if (!run) return null;
  if (!["completed", "cookie_expired", "data_loss_detected"].includes(run.status)) return null;
  const total = run.total_orders_found ?? 0;
  const kept = run.placed_by_filter_kept ?? null;
  const dropped = run.placed_by_filter_dropped ?? 0;
  const orders_with_invoice = run.pdfs_downloaded ?? 0;
  const skippedNotYet = run.pdfs_skipped_not_yet ?? 0;
  const skippedNoInvoice = run.pdfs_skipped_no_invoice ?? 0;
  const failed = run.pdfs_failed ?? 0;
  const filterOn = run.placed_by_filter === "me";
  return (
    <div className="rounded-md border bg-muted/30 px-3 py-3 space-y-2" data-testid="completion-panel">
      <div className="text-sm" data-testid="completion-panel-headline">
        <div>
          Amazon returned <span className="font-medium text-foreground">{total}</span> order(s) in this date range.
        </div>
        {filterOn && kept !== null && (
          <div className="mt-1">
            After your &ldquo;only orders I placed&rdquo; filter:{" "}
            <span className="font-medium text-foreground">{kept}</span> kept,{" "}
            <span className="text-muted-foreground">{dropped} filtered out (someone else placed them).</span>
          </div>
        )}
        {filterOn && run.placed_by_filter_short_name_warning && (
          <div
            className="mt-2 rounded-md border border-amber-500/60 bg-amber-50 dark:bg-amber-950/30 px-2 py-1.5 text-xs text-amber-900 dark:text-amber-200"
            data-testid="short-name-warning"
          >
            <strong>We may be missing some of your orders.</strong>{" "}
            Your Settings name{" "}
            {run.placed_by_filter_matched_name && (
              <span>(&ldquo;{run.placed_by_filter_matched_name}&rdquo;) </span>
            )}
            looks like a short version — Amazon shows longer names like
            &ldquo;{run.placed_by_filter_short_name_example}&rdquo; on some orders.
            Update to your full name on{" "}
            <Link to="/settings" className="underline underline-offset-2" data-testid="short-name-warning-settings-link">
              Settings
            </Link>{" "}
            and rerun this range to pick them up.
          </div>
        )}
        {(run.sheet_consistency_orphan_count || 0) > 0 && (
          <div
            className="mt-2 rounded-md border border-amber-500/60 bg-amber-50 dark:bg-amber-950/30 px-2 py-1.5 text-xs text-amber-900 dark:text-amber-200"
            data-testid="sheet-consistency-warning"
          >
            <strong>Sheet consistency mismatch:</strong>{" "}
            {run.sheet_consistency_orphan_count} document row(s) reference an
            Order ID that is not on the Orders sheet.{" "}
            {(run.sheet_consistency_orphan_order_ids || []).slice(0, 5).join(", ")}
            {(run.sheet_consistency_orphan_order_ids || []).length > 5 && "…"}
            <br />
            This usually means a document survived a re-scrape while its
            parent order was renamed or dropped. Re-run this date range
            to fully resolve.
          </div>
        )}
        <div className="mt-1">
          Invoices saved for{" "}
          <span className="font-medium text-emerald-700 dark:text-emerald-400">{orders_with_invoice}</span>{" "}
          order(s). (One order may yield multiple invoice PDFs — one per seller.)
        </div>
      </div>
      {(skippedNotYet > 0 || skippedNoInvoice > 0 || failed > 0) && (
        <ul className="text-xs text-muted-foreground space-y-1 list-disc list-inside" data-testid="completion-panel-bullets">
          {skippedNotYet > 0 && (
            <li data-testid="completion-skipped-not-yet-line">
              <span className="font-medium text-foreground">{skippedNotYet}</span>{" "}
              skipped — Amazon hasn&apos;t generated the invoice yet
              (usually because the order is still being processed). Re-run the same range in a day or two.
            </li>
          )}
          {skippedNoInvoice > 0 && (
            <li data-testid="completion-skipped-no-invoice-line">
              <span className="font-medium text-foreground">{skippedNoInvoice}</span>{" "}
              skipped — order type has no invoice (typically gift cards, digital downloads, or Amazon-fulfilled subscriptions).
              {" "}<span className="italic">
                If real invoices are missing here, click &ldquo;Re-process skipped&rdquo; below — Amazon
                sometimes rate-limits our popover fetches under load.
              </span>
            </li>
          )}
          {failed > 0 && (
            <li data-testid="completion-failed-line">
              <span className="font-medium text-amber-700 dark:text-amber-400">{failed} failed</span> —
              Amazon didn&apos;t serve the invoice PDF in time. Click <span className="font-medium">Re-process failed</span>{" "}
              below to retry only the failures.
            </li>
          )}
        </ul>
      )}
    </div>
  );
}

export default function RunDetail() {
  const { id } = useParams();
  const [run, setRun] = useState(null);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reimportOpen, setReimportOpen] = useState(false);
  const [resumeBusy, setResumeBusy] = useState(false);
  const [pollIntervalId, setPollIntervalId] = useState(null);
  // Phase 7.1.4 — refilter state (regenerates the Excel + zip with
  // the current Settings display name, no re-scrape).
  const [refilterOpen, setRefilterOpen] = useState(false);
  const [refilterBusy, setRefilterBusy] = useState(false);

  const refreshRun = async () => {
    try {
      const [r, o] = await Promise.all([
        api.get(`/runs/${id}`),
        api.get(`/runs/${id}/orders?limit=2000`),
      ]);
      setRun(r.data);
      setOrders(o.data.orders || []);
      return r.data;
    } catch (e) {
      toast.error(detailFromError(e));
      return null;
    }
  };

  useEffect(() => {
    let cancelled = false;
    refreshRun().finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [id]);

  useEffect(() => {
    if (!run) return;
    const inFlight = ["pending", "scraping", "downloading_pdfs", "packaging"].includes(run.status);
    if (!inFlight) {
      if (pollIntervalId) { clearInterval(pollIntervalId); setPollIntervalId(null); }
      return;
    }
    if (pollIntervalId) return;
    const t = setInterval(() => { refreshRun(); }, 1500);
    setPollIntervalId(t);
    return () => clearInterval(t);
  }, [run?.status]);

  const startResume = async () => {
    setResumeBusy(true);
    try {
      const res = await api.post(`/runs/${id}/resume`);
      if (res.data.resumed === false) {
        toast.message(`Nothing to resume — ${res.data.reason || "run already clean"}.`);
      } else {
        toast.success("Resume started — re-downloading missing invoices…");
        await refreshRun();
      }
    } catch (e) {
      const data = e?.response?.data;
      const inner = data?.detail;
      if (inner && typeof inner === "object" && inner.error === "cookies_still_invalid") {
        toast.error("Cookies are no longer valid — paste a fresh export.");
        setReimportOpen(true);
      } else {
        toast.error(detailFromError(e));
      }
    } finally {
      setResumeBusy(false);
    }
  };

  // Phase 7.1.4 — Re-filter with current Settings display name.
  // Regenerates the Excel + zip using the ALREADY-scraped orders —
  // saves the ~10-min re-scrape when the operator just needs to
  // correct their display-name misconfiguration.
  const startRefilter = async () => {
    setRefilterBusy(true);
    try {
      const res = await api.post(`/runs/${id}/refilter`);
      const b = res.data;
      toast.success(
        `Re-filtered: kept ${b.kept}, dropped ${b.dropped}` +
        (b.matched_name ? ` (matched: ${b.matched_name})` : "")
      );
      setRefilterOpen(false);
      await refreshRun();
    } catch (e) {
      toast.error(detailFromError(e));
    } finally {
      setRefilterBusy(false);
    }
  };

  if (loading) return <div className="text-muted-foreground">Loading…</div>;
  if (!run) return <div className="text-muted-foreground">Run not found.</div>;

  const runIsLegacy = !!run.is_legacy_summary_only;
  const downloadable = run.download_ready && (run.status === "completed" || run.status === "cookie_expired");
  const failedCount = run.pdfs_failed || 0;
  const skippedNoInvoiceCount = run.pdfs_skipped_no_invoice || 0;
  const isResumable =
    ["cookie_expired", "failed"].includes(run.status) ||
    (run.status === "completed" && (failedCount + skippedNoInvoiceCount) > 0);
  const inFlight = ["pending", "scraping", "downloading_pdfs", "packaging"].includes(run.status);
  // Phase 7 post-mortem — label prefers "failed" when only failures exist,
  // "skipped" when only skips exist, and combines both when there's a mix.
  // Purely-legacy runs don't get a re-process option because their old
  // pdf pipeline can't be recovered without re-running the whole range.
  const reprocessLabel = (() => {
    if (run.status !== "completed") return "Resume run";
    if (failedCount > 0 && skippedNoInvoiceCount > 0) {
      return `Re-process ${failedCount} failed + ${skippedNoInvoiceCount} skipped`;
    }
    if (failedCount > 0) return `Re-process ${failedCount} failed invoice(s)`;
    if (skippedNoInvoiceCount > 0) return `Re-process ${skippedNoInvoiceCount} skipped invoice(s)`;
    return "Re-process invoices";
  })();

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight" data-testid="run-detail-title">Run detail</h1>
          <p className="text-sm text-muted-foreground font-mono mt-1" data-testid="run-detail-id">{run.id}</p>
        </div>
        <Link to="/runs" className="text-sm text-muted-foreground hover:text-foreground" data-testid="back-history-link">
          ← All runs
        </Link>
      </div>

      {runIsLegacy && (
        <div
          data-testid="legacy-run-banner"
          className="rounded-md border border-amber-500/40 bg-amber-50 px-4 py-3 text-sm text-amber-900 dark:bg-amber-950/30 dark:text-amber-200"
        >
          <div className="flex items-start gap-3">
            <span aria-hidden="true" className="text-lg leading-none">⚠</span>
            <div>
              <div className="font-semibold">Legacy run — contains Order Summaries, not GST invoices</div>
              <p className="mt-1 leading-relaxed">
                This run was created before we shipped the real invoice downloader.
                The PDFs inside its zip are Amazon&apos;s <span className="font-medium">Printable Order Summary</span>{" "}
                page (an order confirmation), <span className="font-medium">NOT</span> the GST tax invoices your
                accountant needs. Re-run the same date range and the app will fetch the real invoices and credit notes.
              </p>
              <p className="mt-2">
                <Link
                  to="/run"
                  className="underline underline-offset-4 font-medium"
                  data-testid="legacy-banner-rerun-link"
                >
                  Go to Run → re-run this date range
                </Link>
              </p>
            </div>
          </div>
        </div>
      )}

      <Card>
        <CardHeader>
          <CardTitle>Summary</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3 text-sm">
          <div className="flex items-center gap-3 flex-wrap">
            <Badge variant={VARIANT[run.status] || "secondary"} className="uppercase tracking-wider" data-testid="detail-status-badge">
              {run.status}
            </Badge>
            <span>{run.start_date} → {run.end_date}</span>
            {run.placed_by_filter === "me" && (
              <span
                className="inline-flex items-center rounded-full border border-border bg-muted px-2 py-0.5 text-xs font-medium"
                data-testid="detail-placed-by-chip"
              >
                only orders I placed
                {run.placed_by_filter_matched_name ? ` (${run.placed_by_filter_matched_name})` : ""}
              </span>
            )}
            {runIsLegacy && (
              <Badge variant="outline" className="border-amber-500/60 text-amber-700 dark:text-amber-400" data-testid="detail-legacy-badge">
                Legacy: order summaries
              </Badge>
            )}
            <span className="text-muted-foreground">started {new Date(run.started_at).toLocaleString()}</span>
            {run.finished_at && (
              <span className="text-muted-foreground">finished {new Date(run.finished_at).toLocaleString()}</span>
            )}
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Stat label="Orders" value={run.total_orders_found ?? 0} testid="detail-stat-orders" />
            <Stat
              label={runIsLegacy ? "Order summaries saved" : "Invoices saved"}
              value={run.pdfs_downloaded ?? 0}
              testid="detail-stat-downloaded"
            />
            <Stat
              label="Skipped (Amazon delay + no-invoice types)"
              value={
                (run.pdfs_skipped_not_yet ?? 0) + (run.pdfs_skipped_no_invoice ?? 0) || (run.pdfs_skipped ?? 0)
              }
              testid="detail-stat-skipped"
            />
            <Stat label="Failed (retryable)" value={run.pdfs_failed ?? 0} testid="detail-stat-failed" />
          </div>

          <CompletionPanel run={run} />

          {downloadable && (
            <div className="flex flex-wrap items-center gap-3 pt-2">
              <Button asChild data-testid="detail-download-button">
                <a
                  href={`${API}/runs/${run.id}/download`}
                  download={`amazon-invoices-${run.id}.zip`}
                >
                  Download .zip ({formatBytes(run.zip_size_bytes || 0)})
                </a>
              </Button>
              {/* Phase 7.1.4 — refilter action. Only surfaces on
                  completed runs where we have both the raw orders
                  AND a meaningful placed_by filter to re-apply. */}
              {!runIsLegacy && run.placed_by_filter === "me" && (
                <Button
                  variant="outline"
                  onClick={() => setRefilterOpen(true)}
                  disabled={refilterBusy}
                  data-testid="detail-refilter-button"
                >
                  {refilterBusy ? "Re-filtering…" : "Re-filter with updated name"}
                </Button>
              )}
              <span className="text-xs text-muted-foreground">
                contains invoices.xlsx + {run.pdfs_downloaded || 0}{" "}
                {runIsLegacy ? "order summary PDF(s)" : "invoice PDF(s)"} + README
              </span>
              {run.refiltered_at && (
                <span
                  className="text-[11px] text-emerald-700 dark:text-emerald-400"
                  data-testid="detail-refiltered-badge"
                  title={`Last re-filtered at ${run.refiltered_at}`}
                >
                  Re-filtered · {run.placed_by_filter_kept ?? 0} kept /{" "}
                  {run.placed_by_filter_dropped ?? 0} dropped
                </span>
              )}
            </div>
          )}
          {(isResumable || inFlight) && (
            <div className="flex flex-wrap items-center gap-3 pt-2" data-testid="resume-row">
              {isResumable && (
                <Button
                  variant="default"
                  onClick={startResume}
                  disabled={resumeBusy}
                  data-testid={
                    run.status === "completed" ? "reprocess-failed-button" : "resume-run-button"
                  }
                >
                  {reprocessLabel}
                </Button>
              )}
              <Button
                variant="outline"
                onClick={() => setReimportOpen(true)}
                disabled={resumeBusy}
                data-testid="reimport-cookies-button"
              >
                Re-import cookies
              </Button>
              <span className="text-xs text-muted-foreground">
                {run.status === "cookie_expired" && "Cookies expired mid-run — paste a fresh export then resume."}
                {run.status === "failed" && "Run failed — re-import cookies if needed, then click Resume."}
                {run.status === "completed" && (failedCount + skippedNoInvoiceCount) > 0 && (
                  <>
                    Re-process retries only failed + skipped orders. Existing successful invoices are kept and the zip is rebuilt.
                    {skippedNoInvoiceCount > 0 && (
                      <>{" "}Amazon sometimes rate-limits us and drops the invoice link from a popover — a second pass usually recovers it.</>
                    )}
                  </>
                )}
                {inFlight && "Run in progress…"}
              </span>
            </div>
          )}
          {run.error && (
            <div className="text-destructive break-words" data-testid="detail-error">Error: {run.error}</div>
          )}
        </CardContent>
      </Card>

      <CookieReimportDialog
        open={reimportOpen}
        onOpenChange={setReimportOpen}
        onVerified={() => refreshRun()}
      />

      {/* Phase 7.1.4 — confirm-modal for the Re-filter action. */}
      <AlertDialog open={refilterOpen} onOpenChange={setRefilterOpen}>
        <AlertDialogContent data-testid="refilter-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Re-filter with updated name?</AlertDialogTitle>
            <AlertDialogDescription>
              This will re-apply the &ldquo;Only orders I placed myself&rdquo;
              filter using your current Settings display name
              {run?.placed_by_filter_matched_name && (
                <> (last matched: <span className="font-medium">&ldquo;{run.placed_by_filter_matched_name}&rdquo;</span>)</>
              )}
              , then regenerate the Excel and .zip from the orders we already
              scraped. No new browsing on amazon.in.
              <div className="mt-2 text-xs text-muted-foreground">
                Current counts: {run?.placed_by_filter_kept ?? 0} kept /{" "}
                {run?.placed_by_filter_dropped ?? 0} dropped.
                Update your name on <Link to="/settings" className="underline">Settings</Link> first
                if it&apos;s stale.
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="refilter-cancel-button">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={startRefilter}
              disabled={refilterBusy}
              data-testid="refilter-confirm-button"
            >
              {refilterBusy ? "Re-filtering…" : "Yes, re-filter"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Card>
        <CardHeader>
          <CardTitle>Orders</CardTitle>
        </CardHeader>
        <CardContent>
          {orders.length === 0 ? (
            <div className="text-muted-foreground" data-testid="orders-empty">No orders found in this range.</div>
          ) : (
            <Table data-testid="orders-table">
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Order ID</TableHead>
                  <TableHead>Total</TableHead>
                  <TableHead>Items</TableHead>
                  <TableHead>Ship to</TableHead>
                  <TableHead>Placed by</TableHead>
                  <TableHead>PO#</TableHead>
                  <TableHead>PDFs</TableHead>
                  <TableHead className="max-w-[280px]">Why?</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {orders.map((o) => (
                  <TableRow
                    key={o.id}
                    data-testid={`order-row-${o.order_id}`}
                    className={o.filtered_out ? "opacity-60" : ""}
                  >
                    <TableCell className="text-sm">{o.order_date}</TableCell>
                    <TableCell className="font-mono text-xs">{o.order_id}</TableCell>
                    <TableCell className="text-sm">{o.order_total_str || "—"}</TableCell>
                    <TableCell className="text-sm max-w-xs truncate" title={(o.items || []).map((it) => it.title).join(" / ")}>
                      {(o.items || []).slice(0, 2).map((it) => it.title).filter(Boolean).join(" / ") || `${o.item_count} item(s)`}
                    </TableCell>
                    <TableCell className="text-sm">{o.ship_to_name || "—"}</TableCell>
                    <TableCell className="text-sm">{o.placed_by || "—"}</TableCell>
                    <TableCell className="text-sm">{o.po_number || "—"}</TableCell>
                    <TableCell>
                      <PdfCell runId={run.id} order={o} runIsLegacy={runIsLegacy} />
                    </TableCell>
                    <TableCell className="max-w-[280px] align-top whitespace-normal">
                      <PdfReasonCell order={o} />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function Stat({ label, value, testid }) {
  return (
    <div className="rounded-md border p-3">
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className="text-base font-medium" data-testid={testid}>{String(value)}</div>
    </div>
  );
}
